import Actions from 'actions'
import CategoryItems from 'components/Patients/PatientGuidelines/CategoryItems'
import SetupElasticsPopup from 'components/Patients/PatientBrief/CategoryComponents/Actions/Elastics/SetupElasticsPopup'
import { GuidelineCategories, PatientBriefActions } from 'consts/hiToolsConsts'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useRolePermissions from 'hooks/useRolePermissions'
import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { trackEvent } from 'utils/analyticsUtils'

/**
 * Drop-in replacement for `<CategoryItems>` for the Copilot tooltip's "Elastics" section.
 *
 * Renders the same `CategoryItems` as any other category, plus an `onOpenSetup` callback
 * injected into `Elastics` so each elastic line gets an eye icon. Clicking any eye opens
 * the standard `SetupElasticsPopup` anchored to that eye — in edit mode for users with
 * `permissions.updatePatientBrief`, in read-only view mode otherwise. Saves dispatch
 * `Actions.createPatientBrief` directly so the brief modal never appears.
 *
 * @param {Object} props
 * @param {{ component: React.ComponentType, data: { items: Object, note?: string } }} props.categoryData
 *   The `patientBrief` half of a Copilot category entry — same shape passed to
 *   `<CategoryItems>` for any other category.
 */
const ElasticsSetupAction = ({ categoryData }) => {
  const dispatch = useDispatch()
  const { permissions } = useRolePermissions()
  const { copilotControl: copilotControlFF } = useFeatureFlags()

  const patientId = useSelector(state => state.patientsReducer.patient?.id)
  const patientBriefData = useSelector(state => state.patientsReducer.patientBrief?.data)

  const latestPatientBrief = useMemo(
    () => ({
      ...patientBriefData?.[0],
      categories: JSON.parse(patientBriefData?.[0]?.categories || '{}')
    }),
    [patientBriefData]
  )

  const currentElastics = useMemo(
    () => latestPatientBrief.categories?.[GuidelineCategories.Elastics]?.items?.[PatientBriefActions.Elastics] || [],
    [latestPatientBrief]
  )

  const readOnly = !permissions.updatePatientBrief

  const [anchorEl, setAnchorEl] = useState(null)
  const [isOpen, setIsOpen] = useState(false)

  const handleOpenSetup = useCallback(
    eyeEl => {
      setAnchorEl(eyeEl)
      setIsOpen(true)
      trackEvent('Patient Copilot - elastics setup opened', {
        readOnly,
        elasticsCount: currentElastics.length
      })
    },
    [readOnly, currentElastics.length]
  )

  const handleSave = useCallback(
    newElastics => {
      if (readOnly) {
        return
      }
      const elasticsCategory = latestPatientBrief.categories?.[GuidelineCategories.Elastics] || { items: {} }
      const updatedCategories = {
        ...latestPatientBrief.categories,
        [GuidelineCategories.Elastics]: {
          ...elasticsCategory,
          items: {
            ...elasticsCategory.items,
            [PatientBriefActions.Elastics]: newElastics
          }
        }
      }
      dispatch(
        Actions.createPatientBrief({
          patientId,
          a_doctor: latestPatientBrief.a_doctor,
          doctorId: latestPatientBrief.doctorId,
          copilotControlFF,
          customTitle: latestPatientBrief.customTitle,
          practiceNotes: latestPatientBrief.practiceNotes,
          categories: updatedCategories
        })
      )
    },
    [readOnly, latestPatientBrief, patientId, dispatch, copilotControlFF]
  )

  return (
    <>
      <CategoryItems
        categoryComponent={categoryData.component}
        items={categoryData.data.items}
        note={categoryData.data.note}
        categoryComponentProps={{ onOpenSetup: handleOpenSetup }}
      />
      <SetupElasticsPopup
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        anchorEl={anchorEl}
        selections={currentElastics}
        setSelections={handleSave}
        readOnly={readOnly}
      />
    </>
  )
}

export default ElasticsSetupAction
